import { z } from 'zod'

/**
 * Housecall Pro's work statuses for a job, as the live account returned them
 * on 2026-10-08. HCP publishes no such list, so this is what has been seen,
 * not a contract: HCP can add a status any day. A status not here is read as
 * unrecognized rather than refused (`readHcpWorkStatus`), and the database
 * keeps HCP's own words rather than a Prisma enum, for the same reason.
 */
export const HCP_WORK_STATUSES = [
  'needs scheduling',
  'scheduled',
  'in progress',
  'complete unrated',
  'complete rated',
  'user canceled',
  'pro canceled',
] as const

export const hcpWorkStatusSchema = z.enum(HCP_WORK_STATUSES)

export type HcpWorkStatus = z.infer<typeof hcpWorkStatusSchema>

/** A job the technician finished: the repairs it was for close. */
const FINISHED_HCP_WORK_STATUSES: readonly HcpWorkStatus[] = [
  'complete unrated',
  'complete rated',
]

/** A job nobody will finish: it is shown, and not asked about again. */
export const CANCELED_HCP_WORK_STATUSES: readonly HcpWorkStatus[] = [
  'user canceled',
  'pro canceled',
]

/**
 * A work status as read from HCP: one this CRM knows, or one it does not,
 * kept in HCP's words so a person can still read it. An unrecognized status
 * is neither finished nor canceled, so its job closes nothing and is asked
 * about again, and one new status never fails the rest of a jobs list.
 */
export const hcpWorkStatusReadSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('known'), status: hcpWorkStatusSchema }),
  z.object({ kind: z.literal('unrecognized'), words: z.string() }),
])

export type HcpWorkStatusRead = z.infer<typeof hcpWorkStatusReadSchema>

/** HCP's words for a status, read as one this CRM knows when it is. */
export function readHcpWorkStatus(words: string | null): HcpWorkStatusRead | null {
  if (words === null) return null
  const known = hcpWorkStatusSchema.safeParse(words)
  return known.success
    ? { kind: 'known', status: known.data }
    : { kind: 'unrecognized', words }
}

/** HCP's own words for a status, as stored and shown. */
export function hcpWorkStatusWords(read: HcpWorkStatusRead): string {
  return read.kind === 'known' ? read.status : read.words
}

/** Whether HCP says the technician finished the job. */
export function isHcpWorkFinished(read: HcpWorkStatusRead | null): boolean {
  return read?.kind === 'known' && FINISHED_HCP_WORK_STATUSES.includes(read.status)
}

/** Whether HCP says the job was canceled. */
export function isHcpWorkCanceled(read: HcpWorkStatusRead | null): boolean {
  return read?.kind === 'known' && CANCELED_HCP_WORK_STATUSES.includes(read.status)
}
